import { Routes, Route, Navigate } from "react-router";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Layout } from "@/components/Layout";
import { Login } from "@/pages/Login";
import { Signup } from "@/pages/Signup";
import { SignupVerify } from "@/pages/SignupVerify";
import { SignupFinish } from "@/pages/SignupFinish";
import { Join } from "@/pages/Join";
import { Handoff } from "@/pages/Handoff";
import { ForgotPassword } from "@/pages/ForgotPassword";
import { ResetPassword } from "@/pages/ResetPassword";
import { Settings } from "@/pages/Settings";
import { Team } from "@/pages/Team";
import { Overview } from "@/pages/Overview";
import { Requests } from "@/pages/Requests";
import { Routing } from "@/pages/Routing";
import { RoutingPolicyDetail } from "@/pages/RoutingPolicyDetail";
import { RoutingDryRun } from "@/pages/RoutingDryRun";
import { Governance } from "@/pages/Governance";
import { PromptStability } from "@/pages/PromptStability";
import { SavingsBreakdown } from "@/pages/SavingsBreakdown";
import { Billing } from "@/pages/Billing";
import { CurrentPeriod } from "@/pages/CurrentPeriod";
import { Statements } from "@/pages/Statements";
import { StatementDetail } from "@/pages/StatementDetail";

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-canvas">
        <div className="text-fg-muted text-sm">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/signup/verify" element={<SignupVerify />} />
      <Route path="/signup/finish" element={<SignupFinish />} />
      {/* Public token-credentialed pages; each scrubs its token from the
          address bar on mount, so also register the bare paths. */}
      <Route path="/join/:token" element={<Join />} />
      <Route path="/join" element={<Join />} />
      <Route path="/handoff/:token" element={<Handoff />} />
      <Route path="/handoff" element={<Handoff />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Overview />} />
        <Route path="requests" element={<Requests />} />
        <Route path="routing" element={<Routing />} />
        <Route path="routing/dry-run" element={<RoutingDryRun />} />
        <Route path="routing/:policyId" element={<RoutingPolicyDetail />} />
        <Route path="savings" element={<SavingsBreakdown />} />
        <Route path="billing" element={<Billing />} />
        <Route path="current-period" element={<CurrentPeriod />} />
        <Route path="statements" element={<Statements />} />
        <Route path="statements/:periodId" element={<StatementDetail />} />
        <Route path="governance" element={<Governance />} />
        <Route path="prompt-stability" element={<PromptStability />} />
        <Route path="settings" element={<Settings />} />
        <Route path="team" element={<Team />} />
      </Route>
    </Routes>
  );
}

export function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}
